import * as i1 from '@angular/cdk/overlay';
import { Overlay, CdkOverlayOrigin, CdkConnectedOverlay, OverlayModule } from '@angular/cdk/overlay';
import { NgClass, AsyncPipe, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { InjectionToken, EventEmitter, booleanAttribute, numberAttribute, Component, ViewEncapsulation, ChangeDetectionStrategy, Optional, Inject, Self, Attribute, ContentChildren, Input, ViewChild, Output, HostListener, NgModule } from '@angular/core';
import * as i2 from '@sbb-esta/angular/core';
import { mixinVariant, _ErrorStateTracker, getOptionScrollPosition, SBB_OPTION_PARENT_COMPONENT, SbbOption, SbbOptgroup, SbbCommonModule, SbbOptionModule } from '@sbb-esta/angular/core';
import { SbbIcon, SbbIconModule } from '@sbb-esta/angular/icon';
import * as i4 from '@angular/cdk/a11y';
import { ActiveDescendantKeyManager } from '@angular/cdk/a11y';
import { SelectionModel } from '@angular/cdk/collections';
import { DOWN_ARROW, UP_ARROW, LEFT_ARROW, RIGHT_ARROW, ENTER, SPACE, hasModifierKey, A } from '@angular/cdk/keycodes';
import * as i3 from '@angular/forms';
import { Validators } from '@angular/forms';
import * as i5 from '@sbb-esta/angular/form-field';
import { SBB_FORM_FIELD, SbbFormFieldControl } from '@sbb-esta/angular/form-field';
import { Subject, defer, merge } from 'rxjs';
import { startWith, switchMap, take, filter, map, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { trigger, state, style, transition, animate } from '@angular/animations';

/**
 * The following are all the animations for the sbb-select component, with each
 * const containing the metadata for one animation.
 *
 * @docs-private
 */
const sbbSelectAnimations = {
    /**
     * This animation transforms the select's overlay panel on and off the page.
     *
     * When the panel is attached to the DOM, it fades in and when the panel is removed, it fades out.
     */
    transformPanel: trigger('transformPanel', [
        state('void', style({
            opacity: 0,
        })),
        state('showing', style({
            opacity: 1,
        })),
        state('showing-multiple', style({
            opacity: 1,
        })),
        transition('void => *', animate('120ms cubic-bezier(0, 0, 0.2, 1)')),
        transition('* => void', animate('100ms 25ms linear', style({ opacity: 0 }))),
    ]),
};

/**
 * Returns an exception to be thrown when attempting to change a select's `multiple` option
 * after initialization.
 * @docs-private
 */
function getSbbSelectDynamicMultipleError() {
    return Error('Cannot change `multiple` mode of select after initialization.');
}
/**
 * Returns an exception to be thrown when attempting to assign a non-array value to a select
 * in `multiple` mode. Note that `undefined` and `null` are still valid values to allow for
 * resetting the value.
 * @docs-private
 */
function getSbbSelectNonArrayValueError() {
    return Error('Value must be an array in multiple-selection mode.');
}
/**
 * Returns an exception to be thrown when assigning a non-function value to the comparator
 * used to determine if a value corresponds to an option. Note that whether the function
 * actually takes two values and returns a boolean is not checked.
 * @docs-private
 */
function getSbbSelectNonFunctionValueError() {
    return Error('`compareWith` must be a function.');
}

let nextUniqueId = 0;
/**
 * The following style constants are necessary to save here in order
 * to properly calculate the alignment of the selected option over
 * the trigger element.
 */
/** Injection token that determines the scroll handling while a select is open. */
const SBB_SELECT_SCROLL_STRATEGY = new InjectionToken('sbb-select-scroll-strategy');
/** @docs-private */
function SBB_SELECT_SCROLL_STRATEGY_PROVIDER_FACTORY(overlay) {
    return () => overlay.scrollStrategies.reposition();
}
/** Injection token that can be used to provide the default options the select module. */
const SBB_SELECT_CONFIG = new InjectionToken('SBB_SELECT_CONFIG');
/** @docs-private */
const SBB_SELECT_SCROLL_STRATEGY_PROVIDER = {
    provide: SBB_SELECT_SCROLL_STRATEGY,
    deps: [Overlay],
    useFactory: SBB_SELECT_SCROLL_STRATEGY_PROVIDER_FACTORY,
};
/** Change event object that is emitted when the select value has changed. */
class SbbSelectChange {
    constructor(
    /** Reference to the select that emitted the change event. */
    source, 
    /** Current value of the select that emitted the event. */
    value) {
        this.source = source;
        this.value = value;
    }
}
const _SbbSelectMixinBase = mixinVariant(class {
    constructor(_elementRef) {
        this._elementRef = _elementRef;
    }
});
class SbbSelect extends _SbbSelectMixinBase {
    /** Whether the select is focused. */
    get focused() {
        return this._focused || this._panelOpen;
    }
    get placeholder() {
        return this._placeholder;
    }
    set placeholder(value) {
        this._placeholder = value;
        this.stateChanges.next();
    }
    /** Whether the component is required. */
    get required() {
        return this._required ?? this.ngControl?.control?.hasValidator(Validators.required) ?? false;
    }
    set required(value) {
        this._required = value;
        this.stateChanges.next();
    }
    /** Whether the user should be allowed to select multiple options. */
    get multiple() {
        return this._multiple;
    }
    set multiple(value) {
        if (this._selectionModel && (typeof ngDevMode === 'undefined' || ngDevMode)) {
            throw getSbbSelectDynamicMultipleError();
        }
        this._multiple = value;
    }
    /** Whether the element is readonly. */
    get readonly() {
        return this._readonly;
    }
    set readonly(value) {
        this._readonly = value;
        this.stateChanges.next();
    }
    /**
     * Function to compare the option values with the selected values. The first argument
     * is a value from an option. The second is a value from the selection. A boolean
     * should be returned.
     */
    get compareWith() {
        return this._compareWith;
    }
    set compareWith(fn) {
        if (typeof fn !== 'function' && (typeof ngDevMode === 'undefined' || ngDevMode)) {
            throw getSbbSelectNonFunctionValueError();
        }
        this._compareWith = fn;
        if (this._selectionModel) {
            // A different comparator means the selection could change.
            this._initializeSelection();
        }
    }
    /** Value of the select control. */
    get value() {
        return this._value;
    }
    set value(newValue) {
        const hasAssigned = this._assignValue(newValue);
        if (hasAssigned) {
            this._onChange(newValue);
        }
    }
    /** Object used to control when error messages are shown. */
    get errorStateMatcher() {
        return this._errorStateTracker.matcher;
    }
    set errorStateMatcher(value) {
        this._errorStateTracker.matcher = value;
    }
    /** Unique id of the element. */
    get id() {
        return this._id;
    }
    set id(value) {
        this._id = value || this._uid;
        this.stateChanges.next();
    }
    /** Whether the select is in an error state. */
    get errorState() {
        return this._errorStateTracker.errorState;
    }
    set errorState(value) {
        this._errorStateTracker.errorState = value;
    }
    constructor(_viewportRuler, _changeDetectorRef, _ngZone, defaultErrorStateMatcher, elementRef, parentForm, parentFormGroup, _parentFormField, ngControl, tabIndex, scrollStrategyFactory, _liveAnnouncer, _defaultOptions) {
        super(elementRef);
        this._viewportRuler = _viewportRuler;
        this._changeDetectorRef = _changeDetectorRef;
        this._ngZone = _ngZone;
        this._parentFormField = _parentFormField;
        this.ngControl = ngControl;
        this._liveAnnouncer = _liveAnnouncer;
        this._defaultOptions = _defaultOptions;
        /** The scroll position of the overlay panel, calculated to center the selected option. */
        this._scrollTop = 0;
        /** The cached font-size of the trigger element. */
        this._triggerFontSize = 0;
        /** The value of the select panel's transform-origin property. */
        this._transformOrigin = 'top';
        /**
         * The y-offset of the overlay panel in relation to the trigger's top start corner.
         * This must be adjusted to align the selected option text over the trigger text.
         * when the panel opens. Will change based on the y-position of the selected option.
         */
        this._offsetY = 0;
        /**
         * This position config ensures that the top "start" corner of the overlay
         * is aligned with with the top "start" of the origin by default (overlapping
         * the trigger completely). If the panel cannot fit below the trigger, it
         * will fall back to a position above the trigger.
         */
        this._positions = [
            {
                originX: 'start',
                originY: 'bottom',
                overlayX: 'start',
                overlayY: 'top',
            },
            {
                originX: 'start',
                originY: 'top',
                overlayX: 'start',
                overlayY: 'bottom',
            },
        ];
        /** Whether or not the overlay panel is open. */
        this._panelOpen = false;
        /** Comparison function to specify which option is displayed. Defaults to object equality. */
        this._compareWith = (o1, o2) => o1 === o2;
        /** Unique id for this input. */
        this._uid = `sbb-select-${nextUniqueId++}`;
        /** Current `aria-labelledby` value for the select trigger. */
        this._triggerAriaLabelledBy = null;
        /** Emits whenever the component is destroyed. */
        this._destroy = new Subject();
        /**
         * Emits whenever the component state changes and should cause the parent
         * form-field to update. Implemented as part of `SbbFormFieldControl`.
         * @docs-private
         */
        this.stateChanges = new Subject();
        /**
         * Disable the automatic labeling to avoid issues like #1918.
         * @docs-private
         */
        this.disableAutomaticLabeling = true;
        /** `View -> model callback called when value changes` */
        this._onChange = () => { };
        /** `View -> model callback called when select has been touched` */
        this._onTouched = () => { };
        /** ID for the DOM node containing the select's value. */
        this._valueId = `sbb-select-value-${nextUniqueId++}`;
        /** Emits when the panel element is finished transforming in. */
        this._panelDoneAnimatingStream = new Subject();
        this._overlayPanelClass = this._defaultOptions?.overlayPanelClass || 'sbb-overlay-panel';
        this._focused = false;
        /** A name for this control that can be used by `sbb-form-field`. */
        this.controlType = 'sbb-select';
        /** Whether the select is disabled. */
        this.disabled = false; /** Placeholder to be shown if no value has been selected. */
        /** Tab index of the select. */
        this.tabIndex = 0;
        this._multiple = false;
        this._readonly = false;
        /** Aria label of the select. If not specified, the placeholder will be used as label. */
        this.ariaLabel = '';
        /** Combined stream of all of the child options' change events. */
        this.optionSelectionChanges = defer(() => {
            const options = this.options;
            if (options) {
                return options.changes.pipe(startWith(options), switchMap(() => merge(...options.map((option) => option.onSelectionChange))));
            }
            return this._ngZone.onStable.pipe(take(1), switchMap(() => this.optionSelectionChanges));
        });
        /** Event emitted when the select panel has been toggled. */
        this.openedChange = new EventEmitter();
        /** Event emitted when the select has been opened. */
        this._openedStream = this.openedChange.pipe(filter((o) => o), map(() => { }));
        /** Event emitted when the select has been closed. */
        this._closedStream = this.openedChange.pipe(filter((o) => !o), map(() => { }));
        /** Event emitted when the selected value has been changed by the user. */
        this.selectionChange = new EventEmitter();
        /**
         * Event that emits whenever the raw value of the select changes. This is here primarily
         * to facilitate the two-way binding for the `value` input.
         * @docs-private
         */
        this.valueChange = new EventEmitter();
        if (this.ngControl) {
            // Note: we provide the value accessor through here, instead of
            // the `providers` to avoid running into a circular import.
            this.ngControl.valueAccessor = this;
        }
        // Note that we only want to set this when the defaults pass it in, otherwise it should
        // stay as `undefined` so that it falls back to the default in the key manager.
        if (_defaultOptions?.typeaheadDebounceInterval != null) {
            this.typeaheadDebounceInterval = _defaultOptions.typeaheadDebounceInterval;
        }
        this._errorStateTracker = new _ErrorStateTracker(defaultErrorStateMatcher, ngControl, parentFormGroup, parentForm, this.stateChanges);
        this._scrollStrategyFactory = scrollStrategyFactory;
        this._scrollStrategy = this._scrollStrategyFactory();
        this.tabIndex = parseInt(tabIndex, 10) || 0;
        // Force setter to be called in case id was not specified.
        this.id = this.id;
        this._overlayOrigin = new CdkOverlayOrigin(elementRef);
    }
    ngOnInit() {
        this._selectionModel = new SelectionModel(this.multiple);
        this.stateChanges.next();
        // We need `distinctUntilChanged` here, because some browsers will
        // fire the animation end event twice for the same animation. See:
        // https://github.com/angular/angular/issues/24084
        this._panelDoneAnimatingStream
            .pipe(distinctUntilChanged(), takeUntil(this._destroy))
            .subscribe(() => this._panelDoneAnimating(this.panelOpen));
        this._viewportRuler
            .change()
            .pipe(takeUntil(this._destroy))
            .subscribe(() => {
            if (this.panelOpen) {
                this._triggerRect = this._elementRef.nativeElement.getBoundingClientRect();
                this._changeDetectorRef.markForCheck();
            }
        });
    }
    ngAfterContentInit() {
        this._initKeyManager();
        this._selectionModel.changed.pipe(takeUntil(this._destroy)).subscribe((event) => {
            event.added.forEach((option) => option.select());
            event.removed.forEach((option) => option.deselect());
        });
        this.options.changes.pipe(startWith(null), takeUntil(this._destroy)).subscribe(() => {
            this._resetOptions();
            this._initializeSelection();
        });
    }
    ngDoCheck() {
        const newAriaLabelledby = this._getTriggerAriaLabelledby();
        const ngControl = this.ngControl;
        // We have to manage setting the `aria-labelledby` ourselves, because part of its value
        // is computed as a result of a content query which can cause this binding to trigger a
        // "changed after checked" error.
        if (newAriaLabelledby !== this._triggerAriaLabelledBy) {
            const element = this._elementRef.nativeElement;
            this._triggerAriaLabelledBy = newAriaLabelledby;
            if (newAriaLabelledby) {
                element.setAttribute('aria-labelledby', newAriaLabelledby);
            }
            else {
                element.removeAttribute('aria-labelledby');
            }
        }
        if (this.ngControl) {
            // The disabled state might go out of sync if the form group is swapped out. See #17860.
            if (this._previousControl !== ngControl.control) {
                if (this._previousControl !== undefined &&
                    ngControl.disabled !== null &&
                    ngControl.disabled !== this.disabled) {
                    this.disabled = ngControl.disabled;
                }
                this._previousControl = ngControl.control;
            }
            this.updateErrorState();
        }
    }
    ngOnChanges(changes) {
        // Updating the disabled state is handled by the input, but we need to additionally let
        // the parent form field know to run change detection when the disabled state changes.
        if (changes['disabled'] || changes['userAriaDescribedBy']) {
            this.stateChanges.next();
        }
        if (changes['typeaheadDebounceInterval'] && this._keyManager) {
            this._keyManager.withTypeAhead(this.typeaheadDebounceInterval);
        }
    }
    ngOnDestroy() {
        this._destroy.next();
        this._destroy.complete();
        this.stateChanges.complete();
        this._keyManager?.destroy();
    }
    /** Toggles the overlay panel open or closed. */
    toggle() {
        this.panelOpen ? this.close() : this.open();
    }
    /** Opens the overlay panel. */
    open() {
        if (this._canOpen()) {
            this._panelOpen = true;
            this._keyManager.withHorizontalOrientation(null);
            this._highlightCorrectOption();
            this._changeDetectorRef.markForCheck();
            this._triggerRect = this._elementRef.nativeElement.getBoundingClientRect();
            // Note: The computed font-size will be a string pixel value (e.g. "16px").
            // `parseInt` ignores the trailing 'px' and converts this to a number.
            this._triggerFontSize = parseInt(getComputedStyle(this._elementRef.nativeElement).fontSize || '0', 10);
            // Set the font size on the panel element once it exists.
            this._ngZone.onStable.pipe(take(1)).subscribe(() => {
                if (this._triggerFontSize &&
                    this.overlayDir.overlayRef &&
                    this.overlayDir.overlayRef.overlayElement) {
                    this.overlayDir.overlayRef.overlayElement.style.fontSize = `${this._triggerFontSize}px`;
                }
                this._calculateOverlayPosition();
            });
        }
    }
    /** Closes the overlay panel and focuses the host element. */
    close() {
        if (this._panelOpen) {
            this._panelOpen = false;
            this._keyManager.withHorizontalOrientation('ltr');
            this._changeDetectorRef.markForCheck();
            this._onTouched();
        }
    }
    /**
     * Sets the select's value. Part of the ControlValueAccessor interface
     * required to integrate with Angular's core forms API.
     *
     * @param value New value to be written to the model.
     */
    writeValue(value) {
        this._assignValue(value);
    }
    /**
     * Saves a callback function to be invoked when the select's value
     * changes from user input. Part of the ControlValueAccessor interface
     * required to integrate with Angular's core forms API.
     *
     * @param fn Callback to be triggered when the value changes.
     */
    registerOnChange(fn) {
        this._onChange = fn;
    }
    /**
     * Saves a callback function to be invoked when the select is blurred
     * by the user. Part of the ControlValueAccessor interface required
     * to integrate with Angular's core forms API.
     *
     * @param fn Callback to be triggered when the component has been touched.
     */
    registerOnTouched(fn) {
        this._onTouched = fn;
    }
    /**
     * Disables the select. Part of the ControlValueAccessor interface required
     * to integrate with Angular's core forms API.
     *
     * @param isDisabled Sets whether the component is disabled.
     */
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
        this._changeDetectorRef.markForCheck();
        this.stateChanges.next();
    }
    /** Whether or not the overlay panel is open. */
    get panelOpen() {
        return this._panelOpen;
    }
    /** The currently selected option. */
    get selected() {
        return this.multiple ? this._selectionModel?.selected || [] : this._selectionModel?.selected[0];
    }
    /** The value displayed in the trigger. */
    get triggerValue() {
        if (this.empty) {
            return '';
        }
        if (this._multiple) {
            const selectedOptions = this._selectionModel.selected.map((option) => option.viewValue);
            return selectedOptions.join(', ');
        }
        return this._selectionModel.selected[0].viewValue;
    }
    /** Refreshes the error state of the select. */
    updateErrorState() {
        this._errorStateTracker.updateErrorState();
    }
    /** Handles all keydown events on the select. */
    _handleKeydown(event) {
        if (!this.disabled) {
            this.panelOpen ? this._handleOpenKeydown(event) : this._handleClosedKeydown(event);
        }
    }
    /** Handles keyboard events while the select is closed. */
    _handleClosedKeydown(event) {
        const keyCode = event.keyCode;
        const isArrowKey = keyCode === DOWN_ARROW ||
            keyCode === UP_ARROW ||
            keyCode === LEFT_ARROW ||
            keyCode === RIGHT_ARROW;
        const isOpenKey = keyCode === ENTER || keyCode === SPACE;
        const manager = this._keyManager;
        // Open the select on ALT + arrow key to match the native <select>
        if ((!manager.isTyping() && isOpenKey && !hasModifierKey(event)) ||
            ((this.multiple || event.altKey) && isArrowKey)) {
            event.preventDefault(); // prevents the page from scrolling down when pressing space
            this.open();
        }
        else if (!this.multiple) {
            const previouslySelectedOption = this.selected;
            manager.onKeydown(event);
            const selectedOption = this.selected;
            // Since the value has changed, we need to announce it ourselves.
            if (selectedOption && previouslySelectedOption !== selectedOption) {
                // We set a duration on the live announcement, because we want the live element to be
                // cleared after a while so that users can't navigate to it using the arrow keys.
                this._liveAnnouncer.announce(selectedOption.viewValue, 10000);
            }
        }
    }
    /** Handles keyboard events when the selected is open. */
    _handleOpenKeydown(event) {
        const manager = this._keyManager;
        const keyCode = event.keyCode;
        const isArrowKey = keyCode === DOWN_ARROW || keyCode === UP_ARROW;
        const isTyping = manager.isTyping();
        if (isArrowKey && event.altKey) {
            // Close the select on ALT + arrow key to match the native <select>
            event.preventDefault();
            this.close();
            // Don't do anything in this case if the user is typing,
            // because the typing sequence can include the space key.
        }
        else if (!isTyping &&
            (keyCode === ENTER || keyCode === SPACE) &&
            manager.activeItem &&
            !hasModifierKey(event)) {
            event.preventDefault();
            manager.activeItem._selectViaInteraction();
        }
        else if (!isTyping && this._multiple && keyCode === A && event.ctrlKey) {
            event.preventDefault();
            const hasDeselectedOptions = this.options.some((opt) => !opt.disabled && !opt.selected);
            this.options.forEach((option) => {
                if (!option.disabled) {
                    hasDeselectedOptions ? option.select() : option.deselect();
                }
            });
        }
        else {
            const previouslyFocusedIndex = manager.activeItemIndex;
            manager.onKeydown(event);
            if (this._multiple &&
                isArrowKey &&
                event.shiftKey &&
                manager.activeItem &&
                manager.activeItemIndex !== previouslyFocusedIndex) {
                manager.activeItem._selectViaInteraction();
            }
        }
    }
    _onFocus() {
        if (!this.disabled) {
            this._focused = true;
            this.stateChanges.next();
        }
    }
    /**
     * Calls the touched callback only if the panel is closed. Otherwise, the trigger will
     * "blur" to the panel when it opens, causing a false positive.
     */
    _onBlur() {
        this._focused = false;
        this._keyManager?.cancelTypeahead();
        if (!this.disabled && !this.panelOpen) {
            this._onTouched();
            this._changeDetectorRef.markForCheck();
            this.stateChanges.next();
        }
    }
    /** Callback that is invoked when the overlay panel has been attached. */
    _onAttached() {
        this.overlayDir.positionChange.pipe(take(1)).subscribe((positions) => {
            if (positions.connectionPair.originY === 'top') {
                this.panel.nativeElement.classList.add('sbb-panel-above');
                this._elementRef.nativeElement.classList.add('sbb-input-with-open-panel-above');
            }
            else {
                this.panel.nativeElement.classList.remove('sbb-panel-above');
                this._elementRef.nativeElement.classList.remove('sbb-input-with-open-panel-above');
            }
            this._changeDetectorRef.detectChanges();
            this._positioningSettled();
        });
    }
    /** Whether the select has a value. */
    get empty() {
        return !this._selectionModel || this._selectionModel.isEmpty();
    }
    _initializeSelection() {
        // Defer setting the value in order to avoid the "Expression
        // has changed after it was checked" errors from Angular.
        Promise.resolve().then(() => {
            if (this.ngControl) {
                this._value = this.ngControl.value;
            }
            this._setSelectionByValue(this._value);
            this.stateChanges.next();
        });
    }
    /**
     * Sets the selected option based on a value. If no option can be
     * found with the designated value, the select trigger is cleared.
     */
    _setSelectionByValue(value) {
        this._selectionModel.selected.forEach((option) => option.setInactiveStyles());
        this._selectionModel.clear();
        if (this.multiple && value) {
            if (!Array.isArray(value) && (typeof ngDevMode === 'undefined' || ngDevMode)) {
                throw getSbbSelectNonArrayValueError();
            }
            value.forEach((currentValue) => this._selectOptionByValue(currentValue));
            this._sortValues();
        }
        else {
            const correspondingOption = this._selectOptionByValue(value);
            // Shift focus to the active item. Note that we shouldn't do this in multiple
            // mode, because we don't know what option the user interacted with last.
            if (correspondingOption) {
                this._keyManager.updateActiveItem(correspondingOption);
            }
            else if (!this.panelOpen) {
                // Otherwise reset the highlighted option. Note that we only want to do this while
                // closed, because doing it while open can shift the user's focus unnecessarily.
                this._keyManager.updateActiveItem(-1);
            }
        }
        this._changeDetectorRef.markForCheck();
    }
    /**
     * Finds and selects and option based on its value.
     * @returns Option that has the corresponding value.
     */
    _selectOptionByValue(value) {
        const correspondingOption = this.options.find((option) => {
            // Skip options that are already in the model. This allows us to handle cases
            // where the same primitive value is selected multiple times.
            if (this._selectionModel.isSelected(option)) {
                return false;
            }
            try {
                // Treat null as a special reset value.
                return option.value != null && this._compareWith(option.value, value);
            }
            catch (error) {
                if (typeof ngDevMode === 'undefined' || ngDevMode) {
                    // Notify developers of errors in their comparator.
                    console.warn(error);
                }
                return false;
            }
        });
        if (correspondingOption) {
            this._selectionModel.select(correspondingOption);
        }
        return correspondingOption;
    }
    /** Assigns a specific value to the select. Returns whether the value has changed. */
    _assignValue(newValue) {
        // Always re-assign an array, because it might have been mutated.
        if (newValue !== this._value || (this._multiple && Array.isArray(newValue))) {
            if (this.options) {
                this._setSelectionByValue(newValue);
            }
            this._value = newValue;
            return true;
        }
        return false;
    }
    /** Sets up a key manager to listen to keyboard events on the overlay panel. */
    _initKeyManager() {
        this._keyManager = new ActiveDescendantKeyManager(this.options)
            .withTypeAhead(this.typeaheadDebounceInterval)
            .withVerticalOrientation()
            .withHorizontalOrientation('ltr')
            .withHomeAndEnd()
            .withPageUpDown()
            .withAllowedModifierKeys(['shiftKey']);
        this._keyManager.tabOut.subscribe(() => {
            if (this.panelOpen) {
                // Select the active item when tabbing away. This is consistent with how the native
                // select behaves. Note that we only want to do this in single selection mode.
                if (!this.multiple && this._keyManager.activeItem) {
                    this._keyManager.activeItem._selectViaInteraction();
                }
                // Restore focus to the trigger before closing. Ensures that the focus
                // position won't be lost if the user got focus into the overlay.
                this.focus();
                this.close();
            }
        });
        this._keyManager.change.subscribe(() => {
            if (this._panelOpen && this.panel) {
                this._scrollOptionIntoView(this._keyManager.activeItemIndex || 0);
            }
            else if (!this._panelOpen && !this.multiple && this._keyManager.activeItem) {
                this._keyManager.activeItem._selectViaInteraction();
            }
        });
    }
    /** Drops current option subscriptions and IDs and resets from scratch. */
    _resetOptions() {
        const changedOrDestroyed = merge(this.options.changes, this._destroy);
        this.optionSelectionChanges.pipe(takeUntil(changedOrDestroyed)).subscribe((event) => {
            this._onSelect(event.source, event.isUserInput);
            if (event.isUserInput && !this.multiple && this._panelOpen) {
                this.close();
                this.focus();
            }
        });
        // Listen to changes in the internal state of the options and react accordingly.
        // Handles cases like the labels of the selected options changing.
        merge(...this.options.map((option) => option._stateChanges))
            .pipe(takeUntil(changedOrDestroyed))
            .subscribe(() => {
            // `_stateChanges` can fire as a result of a change in the label's DOM value which may
            // be the result of an expression changing. We have to use `detectChanges` in order
            // to avoid "changed after checked" errors (see #14793).
            this._changeDetectorRef.detectChanges();
            this.stateChanges.next();
        });
    }
    /** Invoked when an option is clicked. */
    _onSelect(option, isUserInput) {
        const wasSelected = this._selectionModel.isSelected(option);
        if (option.value == null && !this._multiple) {
            option.deselect();
            this._selectionModel.clear();
            if (this.value != null) {
                this._propagateChanges(option.value);
            }
        }
        else {
            if (wasSelected !== option.selected) {
                option.selected
                    ? this._selectionModel.select(option)
                    : this._selectionModel.deselect(option);
            }
            if (isUserInput) {
                this._keyManager.setActiveItem(option);
            }
            if (this.multiple) {
                this._sortValues();
                if (isUserInput) {
                    // In case the user selected the option with their mouse, we
                    // want to restore focus back to the trigger, in order to
                    // prevent the select keyboard controls from clashing with
                    // the ones from `sbb-option`.
                    this.focus();
                }
            }
        }
        if (wasSelected !== this._selectionModel.isSelected(option)) {
            this._propagateChanges();
        }
        this.stateChanges.next();
    }
    /** Sorts the selected values in the selected based on their order in the panel. */
    _sortValues() {
        if (this.multiple) {
            const options = this.options.toArray();
            this._selectionModel.sort((a, b) => {
                return this.sortComparator
                    ? this.sortComparator(a, b, options)
                    : options.indexOf(a) - options.indexOf(b);
            });
            this.stateChanges.next();
        }
    }
    /** Emits change event to set the model value. */
    _propagateChanges(fallbackValue) {
        let valueToEmit = null;
        if (this.multiple) {
            valueToEmit = this.selected.map((option) => option.value);
        }
        else {
            valueToEmit = this.selected ? this.selected.value : fallbackValue;
        }
        this._value = valueToEmit;
        this.valueChange.emit(valueToEmit);
        this._onChange(valueToEmit);
        this.selectionChange.emit(new SbbSelectChange(this, valueToEmit));
        this._changeDetectorRef.markForCheck();
    }
    /**
     * Highlights the selected item. If no option is selected, it will highlight
     * the first item instead.
     */
    _highlightCorrectOption() {
        if (this._keyManager) {
            if (this.empty) {
                this._keyManager.setFirstItemActive();
            }
            else {
                this._keyManager.setActiveItem(this._selectionModel.selected[0]);
            }
        }
    }
    /** Whether the panel is allowed to open. */
    _canOpen() {
        return !this._panelOpen && !this.disabled && this.options?.length > 0 && !this.readonly;
    }
    /** Focuses the select element. */
    focus(options) {
        this._elementRef.nativeElement.focus(options);
    }
    /** Gets the aria-labelledby for the select panel. */
    _getPanelAriaLabelledby() {
        if (this.ariaLabel) {
            return null;
        }
        const labelId = this._parentFormField?.getLabelId();
        const labelExpression = labelId ? labelId + ' ' : '';
        return this.ariaLabelledby ? labelExpression + this.ariaLabelledby : labelId;
    }
    /** Determines the `aria-activedescendant` to be set on the host. */
    _getAriaActiveDescendant() {
        if (this.panelOpen && this._keyManager && this._keyManager.activeItem) {
            return this._keyManager.activeItem.id;
        }
        return null;
    }
    /** Gets the aria-labelledby of the select component trigger. */
    _getTriggerAriaLabelledby() {
        if (this.ariaLabel) {
            return null;
        }
        const labelId = this._parentFormField?.getLabelId();
        let value = (labelId ? labelId + ' ' : '') + this._valueId;
        if (this.ariaLabelledby) {
            value += ' ' + this.ariaLabelledby;
        }
        return value;
    }
    /** Called when the overlay panel is done animating. */
    _panelDoneAnimating(isOpen) {
        if (this.panelOpen) {
            this._scrollTop = 0;
        }
        else {
            this.overlayDir.offsetX = 0;
            this._changeDetectorRef.markForCheck();
        }
        if (!isOpen) {
            this._elementRef.nativeElement.classList.remove('sbb-input-with-open-panel-above');
        }
        this.openedChange.emit(isOpen);
    }
    _getOverlayMinWidth() {
        return this._triggerRect?.width;
    }
    /**
     * Implemented as part of SbbFormFieldControl.
     * @docs-private
     */
    setDescribedByIds(ids) {
        if (ids.length) {
            this._elementRef.nativeElement.setAttribute('aria-describedby', ids.join(' '));
        }
        else {
            this._elementRef.nativeElement.removeAttribute('aria-describedby');
        }
    }
    /**
     * Forward focus if a user clicks on an associated label.
     * Implemented as part of SbbFormFieldControl.
     * @docs-private
     */
    onContainerClick() {
        this.focus();
        this.open();
    }
    /**
     * Calculates the scroll position of the select's overlay panel.
     *
     * Attempts to center the selected option in the panel. If the option is
     * too high or too low in the panel to be scrolled to the center, it clamps the
     * scroll position to the min or max scroll positions respectively.
     */
    _calculateOverlayScroll(selectedOption, scrollBuffer, maxScroll) {
        const itemHeight = selectedOption._getHostElement().offsetHeight;
        const optionOffsetFromScrollTop = selectedOption._getHostElement().offsetTop;
        const halfOptionHeight = itemHeight / 2;
        // Starts at the optionOffsetFromScrollTop, which scrolls the option to the top of the
        // scroll container, then subtracts the scroll buffer to scroll the option down to
        // the center of the overlay panel. Half the option height must be re-added to the
        // scrollTop so the option is centered based on its middle, not its top edge.
        const optimalScrollPosition = optionOffsetFromScrollTop - scrollBuffer + halfOptionHeight;
        return Math.min(Math.max(0, optimalScrollPosition), maxScroll);
    }
    /** Scrolls the active option into view. */
    _scrollOptionIntoView(index) {
        if (index === 0) {
            // Scroll to top if first index is selected. This would ensure that group labels are displayed.
            this.panel.nativeElement.scrollTop = 0;
        }
        else {
            const option = this.options.toArray()[index];
            if (option) {
                const element = option._getHostElement();
                this.panel.nativeElement.scrollTop = getOptionScrollPosition(element.offsetTop, element.offsetHeight, this.panel.nativeElement.scrollTop, this.panel.nativeElement.offsetHeight);
            }
        }
    }
    _positioningSettled() {
        this.panel.nativeElement.scrollTop = this._scrollTop;
    }
    /** Calculates the scroll position */
    _calculateOverlayPosition() {
        if (!this.panel) {
            return;
        }
        const panelHeight = this.panel.nativeElement.clientHeight;
        const scrollContainerHeight = this.panel.nativeElement.scrollHeight;
        // The farthest the panel can be scrolled before it hits the bottom
        const maxScroll = scrollContainerHeight - panelHeight;
        const firstSelectedOption = this._selectionModel.selected[0];
        if (this.empty || this.options.toArray().indexOf(firstSelectedOption) === -1) {
            this._scrollTop = 0;
            return;
        }
        // We must maintain a scroll buffer so the selected option will be scrolled to the
        // center of the overlay panel rather than the top.
        const scrollBuffer = panelHeight / 2;
        this._scrollTop = this._calculateOverlayScroll(firstSelectedOption, scrollBuffer, maxScroll);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbSelect, deps: [{ token: i1.ViewportRuler }, { token: i0.ChangeDetectorRef }, { token: i0.NgZone }, { token: i2.SbbErrorStateMatcher }, { token: i0.ElementRef }, { token: i3.NgForm, optional: true }, { token: i3.FormGroupDirective, optional: true }, { token: SBB_FORM_FIELD, optional: true }, { token: i3.NgControl, optional: true, self: true }, { token: 'tabindex', attribute: true }, { token: SBB_SELECT_SCROLL_STRATEGY }, { token: i4.LiveAnnouncer }, { token: SBB_SELECT_CONFIG, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "17.3.7", type: SbbSelect, isStandalone: true, selector: "sbb-select", inputs: { disabled: ["disabled", "disabled", booleanAttribute], tabIndex: ["tabIndex", "tabIndex", (value) => (value == null ? 0 : numberAttribute(value))], userAriaDescribedBy: ["aria-describedby", "userAriaDescribedBy"], panelClass: "panelClass", placeholder: "placeholder", required: ["required", "required", booleanAttribute], multiple: ["multiple", "multiple", booleanAttribute], readonly: ["readonly", "readonly", booleanAttribute], compareWith: "compareWith", value: "value", ariaLabel: ["aria-label", "ariaLabel"], ariaLabelledby: ["aria-labelledby", "ariaLabelledby"], errorStateMatcher: "errorStateMatcher", typeaheadDebounceInterval: ["typeaheadDebounceInterval", "typeaheadDebounceInterval", numberAttribute], sortComparator: "sortComparator", id: "id" }, outputs: { openedChange: "openedChange", _openedStream: "opened", _closedStream: "closed", selectionChange: "selectionChange", valueChange: "valueChange" }, host: { attributes: { "role": "combobox", "aria-autocomplete": "none", "aria-haspopup": "listbox" }, listeners: { "click": "toggle()", "keydown": "_handleKeydown($event)", "focus": "_onFocus()", "blur": "_onBlur()" }, properties: { "attr.id": "id", "attr.tabindex": "readonly || disabled ? -1 : tabIndex", "attr.aria-controls": "panelOpen ? id + \"-panel\" : null", "attr.aria-expanded": "panelOpen", "attr.aria-label": "ariaLabel || null", "attr.aria-required": "required.toString()", "attr.aria-disabled": "disabled.toString()", "attr.aria-invalid": "errorState", "attr.aria-activedescendant": "_getAriaActiveDescendant()", "class.sbb-disabled": "disabled", "class.sbb-select-invalid": "errorState", "class.sbb-select-required": "required", "class.sbb-select-empty": "empty", "class.sbb-select-multiple": "multiple", "class.sbb-readonly": "readonly", "class.sbb-focused": "focused", "class.sbb-input-with-open-panel": "panelOpen" }, classAttribute: "sbb-select sbb-input-element" }, providers: [
            { provide: SbbFormFieldControl, useExisting: SbbSelect },
            { provide: SBB_OPTION_PARENT_COMPONENT, useExisting: SbbSelect },
        ], queries: [{ propertyName: "options", predicate: SbbOption, descendants: true }, { propertyName: "optionGroups", predicate: SbbOptgroup, descendants: true }], viewQueries: [{ propertyName: "panel", first: true, predicate: ["panel"], descendants: true }, { propertyName: "overlayDir", first: true, predicate: CdkConnectedOverlay, descendants: true }], exportAs: ["sbbSelect"], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<div\n  [attr.id]=\"_valueId\"\n  class=\"sbb-select-value\"\n  aria-hidden=\"true\"\n  [class.sbb-select-value-empty]=\"empty\"\n>\n  @switch (empty) {\n    @case (true) {\n      <span class=\"sbb-select-placeholder\">{{ !readonly ? placeholder || '\\u00A0' : '-' }}</span>\n    }\n    @case (false) {\n      <span class=\"sbb-select-value-text\">\n        <span>{{ triggerValue || '\\u00A0' }}</span>\n      </span>\n    }\n  }\n</div>\n\n@switch (variant | async) {\n  @case ('standard') {\n    <sbb-icon\n      class=\"sbb-icon-scaled sbb-select-arrow-icon\"\n      svgIcon=\"chevron-small-down-small\"\n    ></sbb-icon>\n  }\n  @default {\n    <sbb-icon\n      class=\"sbb-icon-scaled sbb-select-arrow-icon sbb-select-arrow-icon-lean\"\n      svgIcon=\"chevron-right-small\"\n    ></sbb-icon>\n  }\n}\n\n<ng-template\n  cdk-connected-overlay\n  cdkConnectedOverlayLockPosition\n  cdkConnectedOverlayFlexibleDimensions\n  cdkConnectedOverlayHasBackdrop\n  cdkConnectedOverlayBackdropClass=\"cdk-overlay-transparent-backdrop\"\n  [cdkConnectedOverlayPanelClass]=\"_overlayPanelClass\"\n  [cdkConnectedOverlayScrollStrategy]=\"_scrollStrategy\"\n  [cdkConnectedOverlayOrigin]=\"_overlayOrigin\"\n  [cdkConnectedOverlayOpen]=\"panelOpen\"\n  [cdkConnectedOverlayPositions]=\"_positions\"\n  [cdkConnectedOverlayWidth]=\"_getOverlayMinWidth()\"\n  [cdkConnectedOverlayOffsetX]=\"0\"\n  [cdkConnectedOverlayOffsetY]=\"_offsetY\"\n  [cdkConnectedOverlayMinHeight]=\"30\"\n  (backdropClick)=\"close()\"\n  (attach)=\"_onAttached()\"\n  (detach)=\"close()\"\n>\n  <div\n    #panel\n    role=\"listbox\"\n    tabindex=\"-1\"\n    class=\"sbb-panel sbb-panel-padded sbb-select-panel sbb-scrollbar\"\n    [attr.id]=\"id + '-panel'\"\n    [attr.aria-multiselectable]=\"multiple\"\n    [attr.aria-label]=\"ariaLabel || null\"\n    [attr.aria-labelledby]=\"_getPanelAriaLabelledby()\"\n    [ngClass]=\"panelClass\"\n    [@transformPanel]=\"multiple ? 'showing-multiple' : 'showing'\"\n    (@transformPanel.done)=\"_panelDoneAnimatingStream.next($event.toState)\"\n    [style.transformOrigin]=\"_transformOrigin\"\n    [style.font-size.px]=\"_triggerFontSize\"\n    (keydown)=\"_handleKeydown($event)\"\n    [class.sbb-select-multiple]=\"multiple\"\n  >\n    <ng-content></ng-content>\n  </div>\n</ng-template>\n", styles: [".sbb-select{display:inline-block;cursor:pointer;position:relative;text-align:left;padding-right:calc(2.9333333333rem*var(--sbb-scaling-factor))}:where(html.sbb-lean) .sbb-select{padding-right:2.5333333333rem}.sbb-select:is([readonly],.sbb-readonly){cursor:default}.sbb-form-field .sbb-select,.sbb-table th.sbb-header-cell.sbb-table-filter .sbb-select{display:block}.sbb-select-value{overflow:hidden;text-overflow:ellipsis}:where(html.sbb-lean) .sbb-select-value-empty{color:var(--sbb-color-storm)}.sbb-select-arrow-icon{position:absolute;top:50%;transform:translateY(-50%);right:calc(0.6rem*var(--sbb-scaling-factor));color:var(--sbb-select-arrow-icon-color)}:where(html.sbb-lean) .sbb-select-arrow-icon{right:.4rem}.sbb-select:is([readonly],.sbb-readonly) .sbb-select-arrow-icon{display:none}.sbb-select-arrow-icon>svg{transition:transform 120ms cubic-bezier(0, 0, 0.2, 1)}.sbb-input-with-open-panel .sbb-select-arrow-icon>svg{transform:rotate(180deg)}.sbb-icon.sbb-select-arrow-icon-lean>svg{transform:rotate(90deg)}.sbb-input-with-open-panel .sbb-icon.sbb-select-arrow-icon-lean>svg{transform:rotate(-90deg)}.sbb-select-panel :is(.sbb-menu-group+.sbb-menu-group,.sbb-menu-item+.sbb-menu-group,.sbb-menu-group+.sbb-menu-item){border-top:none}:where(html:not(.sbb-lean)) .sbb-select-panel.sbb-select-multiple .sbb-menu-group .sbb-menu-item{padding-left:calc(0.9333333333rem*var(--sbb-scaling-factor))}"], dependencies: [{ kind: "component", type: SbbIcon, selector: "sbb-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon", "id"], exportAs: ["sbbIcon"] }, { kind: "directive", type: CdkConnectedOverlay, selector: "[cdk-connected-overlay], [connected-overlay], [cdkConnectedOverlay]", inputs: ["cdkConnectedOverlayOrigin", "cdkConnectedOverlayPositions", "cdkConnectedOverlayPositionStrategy", "cdkConnectedOverlayOffsetX", "cdkConnectedOverlayOffsetY", "cdkConnectedOverlayWidth", "cdkConnectedOverlayHeight", "cdkConnectedOverlayMinWidth", "cdkConnectedOverlayMinHeight", "cdkConnectedOverlayBackdropClass", "cdkConnectedOverlayPanelClass", "cdkConnectedOverlayViewportMargin", "cdkConnectedOverlayScrollStrategy", "cdkConnectedOverlayOpen", "cdkConnectedOverlayDisableClose", "cdkConnectedOverlayTransformOriginOn", "cdkConnectedOverlayHasBackdrop", "cdkConnectedOverlayLockPosition", "cdkConnectedOverlayFlexibleDimensions", "cdkConnectedOverlayGrowAfterOpen", "cdkConnectedOverlayPush", "cdkConnectedOverlayDisposeOnNavigation"], outputs: ["backdropClick", "positionChange", "attach", "detach", "overlayKeydown", "overlayOutsideClick"], exportAs: ["cdkConnectedOverlay"] }, { kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "pipe", type: AsyncPipe, name: "async" }], animations: [sbbSelectAnimations.transformPanel], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbSelect, decorators: [{
            type: Component,
            args: [{ selector: 'sbb-select', exportAs: 'sbbSelect', inputs: ['disabled', 'tabIndex'], encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, host: {
                        role: 'combobox',
                        'aria-autocomplete': 'none',
                        'aria-haspopup': 'listbox',
                        class: 'sbb-select sbb-input-element',
                        '[attr.id]': 'id',
                        '[attr.tabindex]': 'readonly || disabled ? -1 : tabIndex',
                        '[attr.aria-controls]': 'panelOpen ? id + "-panel" : null',
                        '[attr.aria-expanded]': 'panelOpen',
                        '[attr.aria-label]': 'ariaLabel || null',
                        '[attr.aria-required]': 'required.toString()',
                        '[attr.aria-disabled]': 'disabled.toString()',
                        '[attr.aria-invalid]': 'errorState',
                        '[attr.aria-activedescendant]': '_getAriaActiveDescendant()',
                        '[class.sbb-disabled]': 'disabled',
                        '[class.sbb-select-invalid]': 'errorState',
                        '[class.sbb-select-required]': 'required',
                        '[class.sbb-select-empty]': 'empty',
                        '[class.sbb-select-multiple]': 'multiple',
                        '[class.sbb-readonly]': 'readonly',
                        '[class.sbb-focused]': 'focused',
                        '[class.sbb-input-with-open-panel]': 'panelOpen',
                    }, animations: [sbbSelectAnimations.transformPanel], providers: [
                        { provide: SbbFormFieldControl, useExisting: SbbSelect },
                        { provide: SBB_OPTION_PARENT_COMPONENT, useExisting: SbbSelect },
                    ], standalone: true, imports: [SbbIcon, CdkConnectedOverlay, NgClass, AsyncPipe], template: "<div\n  [attr.id]=\"_valueId\"\n  class=\"sbb-select-value\"\n  aria-hidden=\"true\"\n  [class.sbb-select-value-empty]=\"empty\"\n>\n  @switch (empty) {\n    @case (true) {\n      <span class=\"sbb-select-placeholder\">{{ !readonly ? placeholder || '\\u00A0' : '-' }}</span>\n    }\n    @case (false) {\n      <span class=\"sbb-select-value-text\">\n        <span>{{ triggerValue || '\\u00A0' }}</span>\n      </span>\n    }\n  }\n</div>\n\n@switch (variant | async) {\n  @case ('standard') {\n    <sbb-icon\n      class=\"sbb-icon-scaled sbb-select-arrow-icon\"\n      svgIcon=\"chevron-small-down-small\"\n    ></sbb-icon>\n  }\n  @default {\n    <sbb-icon\n      class=\"sbb-icon-scaled sbb-select-arrow-icon sbb-select-arrow-icon-lean\"\n      svgIcon=\"chevron-right-small\"\n    ></sbb-icon>\n  }\n}\n\n<ng-template\n  cdk-connected-overlay\n  cdkConnectedOverlayLockPosition\n  cdkConnectedOverlayFlexibleDimensions\n  cdkConnectedOverlayHasBackdrop\n  cdkConnectedOverlayBackdropClass=\"cdk-overlay-transparent-backdrop\"\n  [cdkConnectedOverlayPanelClass]=\"_overlayPanelClass\"\n  [cdkConnectedOverlayScrollStrategy]=\"_scrollStrategy\"\n  [cdkConnectedOverlayOrigin]=\"_overlayOrigin\"\n  [cdkConnectedOverlayOpen]=\"panelOpen\"\n  [cdkConnectedOverlayPositions]=\"_positions\"\n  [cdkConnectedOverlayWidth]=\"_getOverlayMinWidth()\"\n  [cdkConnectedOverlayOffsetX]=\"0\"\n  [cdkConnectedOverlayOffsetY]=\"_offsetY\"\n  [cdkConnectedOverlayMinHeight]=\"30\"\n  (backdropClick)=\"close()\"\n  (attach)=\"_onAttached()\"\n  (detach)=\"close()\"\n>\n  <div\n    #panel\n    role=\"listbox\"\n    tabindex=\"-1\"\n    class=\"sbb-panel sbb-panel-padded sbb-select-panel sbb-scrollbar\"\n    [attr.id]=\"id + '-panel'\"\n    [attr.aria-multiselectable]=\"multiple\"\n    [attr.aria-label]=\"ariaLabel || null\"\n    [attr.aria-labelledby]=\"_getPanelAriaLabelledby()\"\n    [ngClass]=\"panelClass\"\n    [@transformPanel]=\"multiple ? 'showing-multiple' : 'showing'\"\n    (@transformPanel.done)=\"_panelDoneAnimatingStream.next($event.toState)\"\n    [style.transformOrigin]=\"_transformOrigin\"\n    [style.font-size.px]=\"_triggerFontSize\"\n    (keydown)=\"_handleKeydown($event)\"\n    [class.sbb-select-multiple]=\"multiple\"\n  >\n    <ng-content></ng-content>\n  </div>\n</ng-template>\n", styles: [".sbb-select{display:inline-block;cursor:pointer;position:relative;text-align:left;padding-right:calc(2.9333333333rem*var(--sbb-scaling-factor))}:where(html.sbb-lean) .sbb-select{padding-right:2.5333333333rem}.sbb-select:is([readonly],.sbb-readonly){cursor:default}.sbb-form-field .sbb-select,.sbb-table th.sbb-header-cell.sbb-table-filter .sbb-select{display:block}.sbb-select-value{overflow:hidden;text-overflow:ellipsis}:where(html.sbb-lean) .sbb-select-value-empty{color:var(--sbb-color-storm)}.sbb-select-arrow-icon{position:absolute;top:50%;transform:translateY(-50%);right:calc(0.6rem*var(--sbb-scaling-factor));color:var(--sbb-select-arrow-icon-color)}:where(html.sbb-lean) .sbb-select-arrow-icon{right:.4rem}.sbb-select:is([readonly],.sbb-readonly) .sbb-select-arrow-icon{display:none}.sbb-select-arrow-icon>svg{transition:transform 120ms cubic-bezier(0, 0, 0.2, 1)}.sbb-input-with-open-panel .sbb-select-arrow-icon>svg{transform:rotate(180deg)}.sbb-icon.sbb-select-arrow-icon-lean>svg{transform:rotate(90deg)}.sbb-input-with-open-panel .sbb-icon.sbb-select-arrow-icon-lean>svg{transform:rotate(-90deg)}.sbb-select-panel :is(.sbb-menu-group+.sbb-menu-group,.sbb-menu-item+.sbb-menu-group,.sbb-menu-group+.sbb-menu-item){border-top:none}:where(html:not(.sbb-lean)) .sbb-select-panel.sbb-select-multiple .sbb-menu-group .sbb-menu-item{padding-left:calc(0.9333333333rem*var(--sbb-scaling-factor))}"] }]
        }], ctorParameters: () => [{ type: i1.ViewportRuler }, { type: i0.ChangeDetectorRef }, { type: i0.NgZone }, { type: i2.SbbErrorStateMatcher }, { type: i0.ElementRef }, { type: i3.NgForm, decorators: [{
                    type: Optional
                }] }, { type: i3.FormGroupDirective, decorators: [{
                    type: Optional
                }] }, { type: i5.SbbFormField, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [SBB_FORM_FIELD]
                }] }, { type: i3.NgControl, decorators: [{
                    type: Self
                }, {
                    type: Optional
                }] }, { type: undefined, decorators: [{
                    type: Attribute,
                    args: ['tabindex']
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [SBB_SELECT_SCROLL_STRATEGY]
                }] }, { type: i4.LiveAnnouncer }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [SBB_SELECT_CONFIG]
                }] }], propDecorators: { options: [{
                type: ContentChildren,
                args: [SbbOption, { descendants: true }]
            }], optionGroups: [{
                type: ContentChildren,
                args: [SbbOptgroup, { descendants: true }]
            }], userAriaDescribedBy: [{
                type: Input,
                args: ['aria-describedby']
            }], panel: [{
                type: ViewChild,
                args: ['panel']
            }], overlayDir: [{
                type: ViewChild,
                args: [CdkConnectedOverlay]
            }], panelClass: [{
                type: Input
            }], disabled: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], tabIndex: [{
                type: Input,
                args: [{ transform: (value) => (value == null ? 0 : numberAttribute(value)) }]
            }], placeholder: [{
                type: Input
            }], required: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], multiple: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], readonly: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], compareWith: [{
                type: Input
            }], value: [{
                type: Input
            }], ariaLabel: [{
                type: Input,
                args: ['aria-label']
            }], ariaLabelledby: [{
                type: Input,
                args: ['aria-labelledby']
            }], errorStateMatcher: [{
                type: Input
            }], typeaheadDebounceInterval: [{
                type: Input,
                args: [{ transform: numberAttribute }]
            }], sortComparator: [{
                type: Input
            }], id: [{
                type: Input
            }], openedChange: [{
                type: Output
            }], _openedStream: [{
                type: Output,
                args: ['opened']
            }], _closedStream: [{
                type: Output,
                args: ['closed']
            }], selectionChange: [{
                type: Output
            }], valueChange: [{
                type: Output
            }], toggle: [{
                type: HostListener,
                args: ['click']
            }], _handleKeydown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }], _onFocus: [{
                type: HostListener,
                args: ['focus']
            }], _onBlur: [{
                type: HostListener,
                args: ['blur']
            }] } });

class SbbSelectModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbSelectModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.7", ngImport: i0, type: SbbSelectModule, imports: [CommonModule,
            OverlayModule,
            SbbCommonModule,
            SbbIconModule,
            SbbOptionModule,
            SbbSelect], exports: [SbbOptionModule, OverlayModule, SbbSelect] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbSelectModule, providers: [SBB_SELECT_SCROLL_STRATEGY_PROVIDER], imports: [CommonModule,
            OverlayModule,
            SbbCommonModule,
            SbbIconModule,
            SbbOptionModule,
            SbbSelect, SbbOptionModule, OverlayModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbSelectModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        OverlayModule,
                        SbbCommonModule,
                        SbbIconModule,
                        SbbOptionModule,
                        SbbSelect,
                    ],
                    exports: [SbbOptionModule, OverlayModule, SbbSelect],
                    providers: [SBB_SELECT_SCROLL_STRATEGY_PROVIDER],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SBB_SELECT_CONFIG, SBB_SELECT_SCROLL_STRATEGY, SBB_SELECT_SCROLL_STRATEGY_PROVIDER, SBB_SELECT_SCROLL_STRATEGY_PROVIDER_FACTORY, SbbSelect, SbbSelectChange, SbbSelectModule, getSbbSelectDynamicMultipleError, getSbbSelectNonArrayValueError, getSbbSelectNonFunctionValueError, sbbSelectAnimations };
